import { MenuItem, TextField, TextFieldProps } from '@material-ui/core';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Autocomplete } from '@material-ui/lab';
import { CountryCode } from 'libphonenumber-js/min';

import { getGroupOptionsWithNameHierarchy } from 'containers/Forms/Customers/views/CreateCustomerBeta/utils';
import { getSearchCustomerGroups } from 'reducers/customerSearch';
import { getAllHomeStores } from 'reducers/warehouses';
import countries from 'containers/Login/countries.json';
import { PhoneInput } from 'components/inputs/PhoneInput';

const commonProps: TextFieldProps = {
  fullWidth: true,
};

interface Props {
  value: string | number;
  onChange: (val: string | number) => void;
  label?: string;
}

export function CustomerTypeField({ value, onChange, label }: Props) {
  return (
    <TextField
      {...commonProps}
      label={label}
      select
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
    >
      <MenuItem value="">Select option</MenuItem>
      <MenuItem value="DOMESTIC">DOMESTIC</MenuItem>
      <MenuItem value="EU">EU</MenuItem>
      <MenuItem value="OUTSIDE_EU">OUTSIDE_EU</MenuItem>
    </TextField>
  );
}

export function GroupField({ value, onChange, label }: Props) {
  const customerGroups = useSelector(getSearchCustomerGroups);
  const groupOptionsWithNameHierarchy: any[] = useMemo(
    () => getGroupOptionsWithNameHierarchy(customerGroups),
    [customerGroups],
  );
  return (
    <TextField
      {...commonProps}
      label={label}
      select
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
    >
      <MenuItem value="">Select option</MenuItem>
      {groupOptionsWithNameHierarchy.map(({ name, value }) => (
        <MenuItem key={value} value={value}>
          {name}
        </MenuItem>
      ))}
    </TextField>
  );
}

export const HomeStoreField = ({
  value,
  onChange,
  label = '',
}: {
  value: string | number | null | undefined;
  onChange: (val: string | number | null) => void;
  label: string;
}) => {
  const homeStores = useSelector(getAllHomeStores);
  return (
    <Autocomplete
      disablePortal
      id="homeStoreID"
      options={homeStores.map(store => store.warehouseID)}
      renderInput={params => <TextField {...params} label={label} fullWidth />}
      getOptionLabel={option => {
        const selectedStore = homeStores.find(
          store => store.warehouseID === option,
        );
        return selectedStore ? selectedStore.name : '';
      }}
      onChange={(e, newValue) => onChange(newValue)}
      value={value}
    />
  );
};

export const CountryField = ({
  value,
  onChange,
  label = '',
}: {
  value: string | number | null | undefined;
  onChange: (val: string | number | null) => void;
  label: string;
}) => {
  const options = countries.map(c => c.name);
  return (
    <Autocomplete
      disablePortal
      id="country"
      options={options}
      renderInput={params => <TextField {...params} label={label} fullWidth />}
      onChange={(e, newValue) => onChange(newValue)}
      value={value}
    />
  );
};

export function CheckField({ value, onChange, label }: Props) {
  return (
    <TextField
      {...commonProps}
      label={label}
      select
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
    >
      <MenuItem value="">Select option</MenuItem>
      <MenuItem value={1}>Checked</MenuItem>
      <MenuItem value={0}>Unchecked</MenuItem>
    </TextField>
  );
}

type DefaultPhoneInputState = {
  country: CountryCode | 'unknown' | 'blank';
  value: string;
};

export function DefaultPhoneField({
  value,
  onChange,
  label = '',
}: {
  value: DefaultPhoneInputState | undefined;
  onChange: (action: DefaultPhoneInputState) => void;
  label: string;
}) {
  // Necessary to avoid race conditions because the 'onChange' expects a new value
  // but we want to change it piecewise.
  // Thus, if multiple calls are made before this component has had a chance to rerender
  // and generate new value, then by mutating the "base" value each one will still
  // see the changes of the previous one
  const clone: DefaultPhoneInputState = {
    country: 'blank',
    value: '',
    ...value,
  };

  return (
    <PhoneInput
      label={label}
      onChange={e => {
        clone.value = e.target.value;
        onChange(clone);
      }}
      onChangeCountry={country => {
        clone.country = country;
        onChange(clone);
      }}
      country={value?.country}
      value={value?.value}
    />
  );
}
